/**
 * TM Installation Add-on — Prestige Theme
 * =========================================
 * Intercepts Prestige's product form submit and adds both the mailbox
 * and the installation fee to the cart in a single /cart/add JSON request
 * when the installation checkbox is checked.
 *
 * Works with Prestige's AJAX cart drawer and cart notification.
 * Does NOT modify Prestige's core product-form.js.
 *
 * Targeting: Products tagged "post-mounted", "hand-crafted", or "yard-art" in Shopify.
 */

(function () {
  'use strict';

  /**
   * Find the Prestige cart component (drawer or notification bubble).
   * Prestige uses <cart-drawer> or <cart-notification>.
   */
  function getCartComponent() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  /**
   * Get sections to render from the cart component (Prestige API).
   */
  function getCartSections(cartComponent) {
    if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
      return cartComponent.getSectionsToRender().map(function (s) { return s.id; });
    }
    return [];
  }

  /**
   * Trigger Prestige's cart to re-render after items are added.
   */
  function updateCartUI(cartComponent, responseData) {
    // Publish any theme cart event first.
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'tm-installation',
        cartData: responseData
      });
    }

    // Our multi-item add bypasses Prestige's normal product-form flow.
    // Refresh the actual cart-drawer section, then use the theme's own
    // aria-controls trigger to open it so the drawer always appears immediately.
    var wrapper = document.querySelector('.shopify-section--cart-drawer');
    var sectionId = wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : '';

    var openDrawer = function () {
      var trigger = document.querySelector('[aria-controls="cart-drawer"]');
      if (trigger && typeof trigger.click === 'function') {
        trigger.click();
        return;
      }

      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      if (typeof drawer.show === 'function') drawer.show();
      else if (typeof drawer.open === 'function') drawer.open();
      else {
        drawer.setAttribute('open', '');
        drawer.setAttribute('aria-hidden', 'false');
      }
    };

    if (!sectionId) {
      openDrawer();
      return;
    }

    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    fetch(root + '?sections=' + encodeURIComponent(sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function (res) { return res.json(); })
    .then(function (sections) {
      var html = sections && sections[sectionId];
      if (html && wrapper) {
        var holder = document.createElement('div');
        holder.innerHTML = html.trim();
        var freshWrapper = holder.firstElementChild;
        if (freshWrapper) wrapper.replaceWith(freshWrapper);
      }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(openDrawer);
      });
    })
    .catch(function () {
      openDrawer();
    });
  }

  /**
   * Attach the installation intercept to a product form element.
   */
  function attachToForm(productFormEl) {
    // Only attach once
    if (productFormEl.dataset.tmInstallationAttached) return;
    productFormEl.dataset.tmInstallationAttached = '1';

    var form = productFormEl.querySelector('form[data-type="add-to-cart-form"]')
              || productFormEl.querySelector('form');
    if (!form) return;

    var submitButton = productFormEl.querySelector('[type="submit"][name="add"]')
                      || productFormEl.querySelector('button[type="submit"]');

    productFormEl.addEventListener('submit', function (evt) {
      var checkbox = productFormEl.querySelector('[data-tm-installation]');
      var installationSelected = Boolean(checkbox && checkbox.checked);
      var formId = form.getAttribute('id') || '';
      var postSelector = formId
        ? document.querySelector('[data-tm-required-post-selector][data-form-id="' + CSS.escape(formId) + '"]')
        : null;
      var postVariantId = postSelector ? Number(postSelector.dataset.tmPostVariantId || 0) : 0;

      var numberWidget = [...document.querySelectorAll('[data-tm-paid-numbers]')].find(n => n.dataset.formId === formId);
      var numberVariantId = numberWidget ? Number(numberWidget.dataset.variantId || 0) : 0;
      if (numberWidget && !form.reportValidity()) { evt.preventDefault(); evt.stopImmediatePropagation(); return; }
      var rockWidget = [...document.querySelectorAll('tm-rock-base')].find(n => n.dataset.formId === formId);
      var rockEnabled = Boolean(rockWidget?.querySelector('[data-rock-enabled]')?.checked);
      var rockVariantId = rockEnabled ? Number(rockWidget.dataset.variantId || 0) : 0;
      if (rockEnabled && !rockVariantId) { evt.preventDefault(); evt.stopImmediatePropagation(); rockWidget.scrollIntoView({block:'center',behavior:'smooth'}); return; }
      var coating = [...document.querySelectorAll('[data-tm-coating]')].find(n => n.dataset.formId === formId);
      var coatingVariantId = coating?.checked ? Number(coating.dataset.variantId || 0) : 0;
      if (!installationSelected && !postSelector && !numberVariantId && !rockVariantId && !coatingVariantId) return; // Let Prestige handle normally

      if (postSelector && !postVariantId) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        postSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
        postSelector.classList.add('tm-pm-post-selector--attention');
        window.setTimeout(function () {
          postSelector.classList.remove('tm-pm-post-selector--attention');
        }, 1600);
        return;
      }

      // We're taking over — prevent Prestige's default handler
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Guard against double-submit
      if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

      // Show loading state
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        var spinner = submitButton.querySelector('.loading-overlay__spinner, .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      var formData = new FormData(form);
      var mailboxVariantId = Number(formData.get('id'));
      var mailboxQty = Math.max(1, Number(formData.get('quantity') || 1));
      var installationVariantId = installationSelected
        ? Number(checkbox.dataset.installationVariantId)
        : 0;

      if (!mailboxVariantId || (installationSelected && !installationVariantId)) {
        console.warn('[TM Installation] Missing variant IDs — re-submitting form without installation');
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('loading');
        }
        // evt.preventDefault() was already called above, so we must re-submit explicitly.
        // Temporarily uncheck the checkbox so the TM interceptor skips this re-submission,
        // then restore the checked state after the submit event fires.
        if (checkbox) checkbox.checked = false;
        form.requestSubmit ? form.requestSubmit() : form.submit();
        if (checkbox) checkbox.checked = installationSelected;
        return;
      }

      // Build the mailbox item, preserving any line item properties
      var mailboxItem = { id: mailboxVariantId, quantity: mailboxQty };
      var properties = {};
      for (var pair of formData.entries()) {
        if (pair[0].startsWith('properties[') && pair[1]) {
          properties[pair[0].slice(11, -1)] = pair[1];
        }
      }
      if (Object.keys(properties).length) mailboxItem.properties = properties;

      var sellingPlan = formData.get('selling_plan');
      if (sellingPlan) mailboxItem.selling_plan = Number(sellingPlan);

      var cartComponent = getCartComponent();
      var sections = getCartSections(cartComponent);

      var items = [mailboxItem];
      if (coatingVariantId) items.push({id:coatingVariantId,quantity:mailboxQty,properties:{Mailbox:coating.dataset.mailbox,Service:'Ceramic coating — mailbox exterior'}});
      if (rockVariantId) items.push({id:rockVariantId,quantity:mailboxQty,properties:{Mailbox:rockWidget.dataset.mailbox,'Base selection':rockWidget.dataset.selection}});
      if (numberVariantId) items.push({id:numberVariantId,quantity:mailboxQty * Number(numberWidget.dataset.numberCount || 1),properties:{Mailbox:numberWidget.dataset.mailboxTitle,Address:properties['House Numbers'],Finish:properties['Number Finish'],Placement:numberWidget.dataset.placement}});

      if (postSelector && postVariantId) {
        var postProperties = {
          _required_post_for: postSelector.dataset.tmMailboxTitle || 'Mailbox'
        };
        if (postSelector.dataset.tmPostColor) {
          postProperties['Post Color'] = postSelector.dataset.tmPostColor;
        }
        if (postSelector.dataset.tmPostLabel) {
          postProperties['Post Selection'] = postSelector.dataset.tmPostLabel;
        }

        items.push({
          id: postVariantId,
          quantity: mailboxQty,
          properties: postProperties
        });
      }

      if (installationSelected && installationVariantId) {
        items.push({
          id: installationVariantId,
          quantity: mailboxQty,
          properties: { _mailbox_installation_add_on: 'Yes' }
        });
      }

      if (installationSelected && checkbox.dataset.disposalVariantId) {
        items.push({id:Number(checkbox.dataset.disposalVariantId),quantity:mailboxQty,properties:{Service:checkbox.dataset.concreteRemovalVariantId?'Concrete mailbox haul-away — base price':'Standard mailbox haul-away'}});
      }
      if (installationSelected && checkbox.dataset.concreteRemovalVariantId) {
        items.push({id:Number(checkbox.dataset.concreteRemovalVariantId),quantity:mailboxQty,properties:{Service:'Concrete removal — base surcharge'}});
      }
      if (installationSelected && checkbox.dataset.parcelBaseVariantId) {
        items.push({id:Number(checkbox.dataset.parcelBaseVariantId),quantity:mailboxQty,properties:{Service:'Reinforced concrete base — supply and installation'}});
      }
      // LED upgrade: include all add-ons in the SAME multi-item cart
      // request as installation. The installation capture handler otherwise
      // intercepts the submit before the LED handler can run.
      var ledRoot = document.querySelector('[data-tm-led-widget]');
      var ledEnabled = Boolean(
        ledRoot &&
        !ledRoot.querySelector('[data-led-body]')?.hidden &&
        (formId === ledRoot.dataset.formId)
      );
      if (ledEnabled) {
        var numberInput = document.querySelector(
          '[data-product-handle="' + ledRoot.dataset.ledProductHandle + '"][data-tm-handcrafted-configurator] [data-tm-house-number-input], [data-product-handle="' + ledRoot.dataset.ledProductHandle + '"][data-tm-wall-configurator] [data-tm-house-number-input]'
        );
        var address = numberInput ? numberInput.value.trim() : '';
        var digitCount = (address.match(/[0-9]/g) || []).length;
        var ledPlacement = ledRoot.querySelector('input[name="tm-led-placement"]:checked')?.value || 'Single set';
        var ledBilledDigits = digitCount * (ledPlacement === 'Both sides' ? 2 : 1);
        var ledPower = ledRoot.querySelector('input[name="tm-led-power"]:checked')?.value || 'Hardwired';
        var ledError = ledRoot.querySelector('[data-led-error]');
        if (!digitCount) {
          if (ledError) {
            ledError.textContent = 'Please enter your house numbers above.';
            ledError.hidden = false;
          }
          if (numberInput) numberInput.focus();
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
          return;
        }
        var solarSelected = ledPower === 'Solar';
        var digitVariant = Number(ledRoot.dataset.digitVariant);
        var powerVariant = Number(solarSelected ? ledRoot.dataset.solarVariant : ledPower === 'Converter' ? ledRoot.dataset.converterVariant : ledRoot.dataset.transformerVariant);
        if (!digitVariant || (ledPower !== 'Existing' && !powerVariant)) {
          if (ledError) {
            ledError.textContent = 'The LED upgrade is temporarily unavailable. Please contact us.';
            ledError.hidden = false;
          }
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
          return;
        }
        mailboxItem.properties = mailboxItem.properties || {};
        mailboxItem.properties['LED House Numbers'] = ledBilledDigits + ' illuminated digits replacing standard numbers';
        mailboxItem.properties['LED Power Source'] = ledPower;
        mailboxItem.properties['LED Placement'] = ledPlacement;
        mailboxItem.properties['LED Temperature'] = '4000K';
        items.push({
          id: digitVariant,
          quantity: ledBilledDigits * mailboxQty,
          properties: { Mailbox: ledRoot.dataset.ledProduct, Address: address, Placement: ledPlacement }
        });
        if (ledPower !== 'Existing') items.push({
          id: powerVariant,
          quantity: mailboxQty,
          properties: { Mailbox: ledRoot.dataset.ledProduct, Address: address }
        });
      }


      // Give each configured purchase its own group, including repeated mailbox models.
      const tmGroupId = crypto.randomUUID();
      items.forEach((item, index) => {
        item.properties = item.properties || {};
        item.properties._tm_group = tmGroupId;
        item.properties._tm_role = index === 0 ? 'mailbox' : 'upgrade';
        item.properties._tm_per_mailbox = String(item.quantity / items[0].quantity);
      });

      var requestBody = { items: items };

      if (sections.length) {
        requestBody.sections = sections;
        requestBody.sections_url = window.location.pathname;
      }

      if (cartComponent && typeof cartComponent.setActiveElement === 'function') {
        cartComponent.setActiveElement(document.activeElement);
      }

      fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        if (response.status) {
          // Error from Shopify
          console.error('[TM Installation] Cart add error:', response.description || response.message);
          // Show error in Prestige's error wrapper if available
          var errWrapper = productFormEl.querySelector('.product-form__error-message-wrapper, [data-error-message-wrapper]');
          var errMsg = productFormEl.querySelector('.product-form__error-message, [data-error-message]');
          if (errWrapper && errMsg) {
            errMsg.textContent = response.description || response.message || 'Could not add to cart.';
            errWrapper.removeAttribute('hidden');
          }
          return;
        }

        // Find the mailbox item in response for cart drawer rendering
        var mailboxResponseItem = response.items && response.items.find(function (item) {
          return Number(item.variant_id) === mailboxVariantId;
        });
        var cartResponse = response.items
          ? Object.assign({}, response, {
              id: mailboxResponseItem ? mailboxResponseItem.id : undefined,
              key: mailboxResponseItem ? mailboxResponseItem.key : undefined
            })
          : response;

        updateCartUI(cartComponent, cartResponse);
      })
      .catch(function (err) {
        console.error('[TM Installation] Fetch error:', err);
      })
      .finally(function () {
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('loading');
          var spinner = submitButton.querySelector('.loading-overlay__spinner, .loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      });
    }, true); // useCapture=true so we run before Prestige's listener
  }

  /**
   * Initialize: find all product forms on the page and attach.
   * Also observe for dynamically added forms (quick-add modals, etc.).
   */
  function init() {
    // Attach to existing forms
    document.querySelectorAll('product-form').forEach(attachToForm);

    // Watch for new forms added dynamically
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'PRODUCT-FORM') {
            attachToForm(node);
          }
          node.querySelectorAll && node.querySelectorAll('product-form').forEach(attachToForm);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
